import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { Send, Eye, Mail, CheckCircle2, Clock } from "lucide-react";

const DRAFT_KEY = "rdv_newsletter_draft";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  } catch { return "—"; }
}

export function AdminNewsletterSend() {
  const [subject, setSubject] = useState("");
  const [preheader, setPreheader] = useState("");
  const [body, setBody] = useState("");
  const [mode, setMode] = useState("text");
  const [audience, setAudience] = useState(0);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [tab, setTab] = useState("compose");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [providerStatus, setProviderStatus] = useState(null);
  const [testAddrs, setTestAddrs] = useState([]);
  const [purging, setPurging] = useState(false);
  const [statsLoading, setStatsLoading] = useState(null);

  async function refreshStats(id) {
    setStatsLoading(id);
    try {
      const r = await api.get(`/admin/newsletter/campaigns/${id}/stats`);
      setCampaigns(cs => cs.map(c => c.campaign_id === id ? { ...c, stats: r.data } : c));
      if (r.data.tracked === 0) toast.info("Aucun envoi suivi pour cette campagne");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setStatsLoading(null); }
  }

  const loadTestAddrs = () => api.get("/admin/newsletter/test-addresses").then(r => setTestAddrs(r.data.items || [])).catch(()=>{});
  useEffect(() => { loadTestAddrs(); }, []);

  async function purgeTests() {
    if (!window.confirm(`Supprimer ${testAddrs.length} adresse(s) de test de la liste ?`)) return;
    setPurging(true);
    try {
      const r = await api.post("/admin/newsletter/purge-tests");
      toast.success(`${r.data.deleted} adresse(s) de test supprimée(s)`);
      setTestAddrs([]);
      api.get("/admin/newsletter/audience").then(x => setAudience(x.data.count));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setPurging(false); }
  }

  // Restore draft on mount
  useEffect(() => {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (raw) {
      try {
        const d = JSON.parse(raw);
        setSubject(d.subject || "");
        setPreheader(d.preheader || "");
        setBody(d.body || "");
        setMode(d.mode || "text");
      } catch (e) { console.warn("Brouillon newsletter non restauré :", e?.message); }
    }
    reload();
  }, []);

  // Autosave draft
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ subject, preheader, body, mode }));
  }, [subject, preheader, body, mode]);

  function reload() {
    api.get("/admin/newsletter/audience").then(r => setAudience(r.data.count));
    api.get("/admin/newsletter/campaigns").then(r => setCampaigns(r.data.items || []));
    api.get("/admin/newsletter/provider-status").then(r => setProviderStatus(r.data)).catch(()=>{});
  }

  function payload() {
    return { subject, preheader, body, body_mode: mode };
  }

  async function doPreview() {
    if (!subject.trim() || !body.trim()) return toast.error("Sujet et contenu obligatoires");
    setPreviewing(true);
    try {
      const r = await api.post("/admin/newsletter/preview", payload());
      setPreview(r.data.html);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de prévisualisation");
    } finally { setPreviewing(false); }
  }

  async function doTest() {
    if (!subject.trim() || !body.trim()) return toast.error("Sujet et contenu obligatoires");
    setTesting(true);
    try {
      const r = await api.post("/admin/newsletter/test-send", payload());
      if (r.data.delivered) toast.success(`Envoi test réel à ${r.data.sent_to} ✦`);
      else if (r.data.dry_run) toast.info(`Envoi simulé vers ${r.data.sent_to} — aucun fournisseur configuré (dry-run).`);
      else toast.error(r.data.message || "L'envoi test a échoué.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi test");
    } finally { setTesting(false); }
  }

  async function doSend() {
    if (!subject.trim() || !body.trim()) return toast.error("Sujet et contenu obligatoires");
    setConfirmOpen(false);
    setSending(true);
    try {
      const r = await api.post("/admin/newsletter/send", payload());
      toast.success(`Campagne lancée vers ${r.data.recipients_total} inscrite${r.data.recipients_total>1?"s":""} ✦`);
      // Clear draft after successful send
      localStorage.removeItem(DRAFT_KEY);
      setSubject(""); setPreheader(""); setBody(""); setPreview(null);
      // Refresh list shortly after (background task fills it)
      setTimeout(reload, 1200);
      setTimeout(reload, 4000);
      setTab("history");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally { setSending(false); }
  }

  return (
    <div data-testid="admin-newsletter-send" className="max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-hand text-xl text-[#A3876A]">À toutes vos lectrices</p>
          <h1 className="font-serif text-4xl">Envoyer la newsletter</h1>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wider text-[#7C7064]">Audience actuelle</p>
          <p className="font-serif text-3xl" data-testid="newsletter-audience">{audience}</p>
          <p className="text-xs text-[#7C7064]">inscrite{audience>1?"s":""}</p>
        </div>
      </div>

      {/* Bandeau statut fournisseur email */}
      {providerStatus && (
        <div data-testid="provider-banner" className={`mb-6 p-4 border ${
          providerStatus.provider === "resend" ? "bg-[#F5F0E6] border-[#A3876A]"
          : providerStatus.provider === "emergent" ? "bg-[#FDFBF7] border-[#E8DCC8]"
          : "bg-[#FBEEEA] border-[#C9A18A]"
        }`}>
          <p className="text-xs uppercase tracking-wider text-[#7C7064] mb-1">Fournisseur email</p>
          {providerStatus.provider === "resend" && (
            <>
              <p className="font-serif text-lg text-[#2C2621]">Resend — domaine <em>{providerStatus.domain}</em></p>
              <p className="text-sm text-[#4A4036]">
                Expéditeur : <strong>{providerStatus.from}</strong> · Reply-to : <strong>{providerStatus.reply_to}</strong>
              </p>
              <p className="text-xs text-[#7C7064] mt-2">
                {(providerStatus.domain_status === "verified" || providerStatus.domain_status === "verified_send_only")
                  ? "✓ Domaine vérifié — les envois réels fonctionnent."
                  : "⏳ La vérification du domaine chez Resend doit être terminée. Tant qu'elle est en attente, les envois seront rejetés (403) — vérifiez dans votre dashboard Resend → Domains."}
              </p>
              {providerStatus.tracking && (
                <div data-testid="tracking-status" className="mt-3 pt-3 border-t border-[#E8DCC8] text-xs">
                  {providerStatus.tracking.status === "verified" ? (
                    <p className="text-[#7B8A72]">✓ Suivi des ouvertures et des clics actif (sous-domaine <em>{providerStatus.tracking.cname_name}</em>).</p>
                  ) : (
                    <>
                      <p className="text-[#A86962] font-medium">Statistiques d'ouverture / clics : en attente d'un enregistrement DNS</p>
                      <p className="text-[#4A4036] mt-1">Ajoutez chez votre hébergeur de domaine un enregistrement <strong>CNAME</strong> :</p>
                      <p className="mt-1 font-mono text-[11px] bg-white border border-[#E8DCC8] px-2 py-1 inline-block">
                        {providerStatus.tracking.cname_name} &nbsp;→&nbsp; {providerStatus.tracking.cname_value}
                      </p>
                      <p className="text-[#7C7064] mt-1">Puis dans Resend → Domains → « I've added the records ». Les envois suivants seront alors mesurés.</p>
                    </>
                  )}
                </div>
              )}
            </>
          )}
          {providerStatus.provider === "emergent" && (
            <p className="text-sm text-[#4A4036]">Relais Emergent (partagé). Envois transactionnels uniquement.</p>
          )}
          {providerStatus.provider === "none" && (
            <p className="text-sm text-[#A86962]">Aucun fournisseur email configuré — les envois seront simulés.</p>
          )}
        </div>
      )}

      {testAddrs.length > 0 && (
        <div data-testid="test-addresses-banner" className="mb-6 p-4 border border-[#C9A18A] bg-[#FBEEEA] flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-serif text-lg text-[#2C2621]">{testAddrs.length} adresse{testAddrs.length>1?"s":""} de test dans la liste</p>
            <p className="text-xs text-[#7C7064] mt-1 break-all">{testAddrs.map(t => t.email).join(" · ")}</p>
            <p className="text-xs text-[#7C7064] mt-1">Elles comptent comme « échecs » à chaque envoi. Vous pouvez les retirer en un clic.</p>
          </div>
          <button data-testid="purge-test-addresses" onClick={purgeTests} disabled={purging} className="btn-outline whitespace-nowrap">
            {purging ? "…" : "Nettoyer la liste"}
          </button>
        </div>
      )}

      {/* Onglets */}
      <div className="flex gap-6 border-b border-[#E8DCC8] mb-8">
        <button data-testid="tab-compose" onClick={() => setTab("compose")}
          className={`pb-3 text-sm ${tab==="compose"?"border-b-2 border-[#A3876A] text-[#2C2621]":"text-[#7C7064]"}`}>
          Rédiger
        </button>
        <button data-testid="tab-history" onClick={() => setTab("history")}
          className={`pb-3 text-sm ${tab==="history"?"border-b-2 border-[#A3876A] text-[#2C2621]":"text-[#7C7064]"}`}>
          Historique <span className="ml-1 text-[#A3876A]">({campaigns.length})</span>
        </button>
      </div>

      {tab === "compose" && (
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Composer */}
          <div className="space-y-5">
            <div>
              <label className="text-sm text-[#7C7064] block mb-1">Objet de l'email *</label>
              <input data-testid="nl-subject" className="w-full px-4 py-2.5 bg-white border border-[#E8DCC8] text-[#2C2621]"
                placeholder="ex. Un rituel pour la semaine qui vient"
                value={subject} onChange={e => setSubject(e.target.value)}/>
              <p className="text-xs text-[#7C7064] mt-1">S'affiche dans la boîte de réception.</p>
            </div>
            <div>
              <label className="text-sm text-[#7C7064] block mb-1">Préheader (aperçu)</label>
              <input data-testid="nl-preheader" className="w-full px-4 py-2.5 bg-white border border-[#E8DCC8]"
                placeholder="Petite phrase qui apparaît après l'objet"
                value={preheader} onChange={e => setPreheader(e.target.value)} maxLength={200}/>
              <p className="text-xs text-[#7C7064] mt-1">{preheader.length}/200 — texte gris à côté de l'objet dans Gmail/Outlook.</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-[#7C7064]">Contenu *</label>
                <div className="text-xs">
                  <label className="mr-3">
                    <input type="radio" name="mode" checked={mode==="text"} onChange={()=>setMode("text")} className="mr-1"/>
                    Texte simple
                  </label>
                  <label>
                    <input type="radio" name="mode" checked={mode==="html"} onChange={()=>setMode("html")} className="mr-1"/>
                    HTML
                  </label>
                </div>
              </div>
              <Textarea data-testid="nl-body" rows={14}
                className="w-full bg-white border border-[#E8DCC8] font-mono text-sm"
                placeholder={mode==="text"
                  ? "Bonjour {{prenom}},\n\nUne pensée douce à vous aujourd'hui…\n\n(Une ligne vide sépare deux paragraphes)"
                  : "<p>Bonjour…</p>"}
                value={body} onChange={e => setBody(e.target.value)}/>
              <p className="text-xs text-[#7C7064] mt-1">
                {mode==="text"
                  ? "Une ligne vide = nouveau paragraphe. Le style éditorial (crème, serif, en-tête) est ajouté automatiquement."
                  : "HTML brut : vos balises sont insérées dans le gabarit éditorial."}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button data-testid="nl-preview" onClick={doPreview} disabled={previewing}
                className="flex items-center gap-2 px-4 py-2.5 border border-[#A3876A] text-[#A3876A] hover:bg-[#A3876A] hover:text-white transition-colors text-sm disabled:opacity-50">
                <Eye size={14}/> {previewing ? "…" : "Prévisualiser"}
              </button>
              <button data-testid="nl-test" onClick={doTest} disabled={testing}
                className="flex items-center gap-2 px-4 py-2.5 border border-[#7B8A72] text-[#7B8A72] hover:bg-[#7B8A72] hover:text-white transition-colors text-sm disabled:opacity-50">
                <Mail size={14}/> {testing ? "…" : "Envoyer un test à moi"}
              </button>
              <button data-testid="nl-send" onClick={()=>setConfirmOpen(true)} disabled={sending}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#A3876A] text-white hover:bg-[#8A6E52] transition-colors text-sm disabled:opacity-50 ml-auto">
                <Send size={14}/> {sending ? "Envoi en cours…" : `Envoyer à ${audience} inscrite${audience>1?"s":""}`}
              </button>
            </div>
            <p className="text-xs text-[#7C7064]">Un lien de désinscription est ajouté automatiquement en bas de chaque email — conformité RGPD.</p>
          </div>

          {/* Preview */}
          <div className="bg-[#F5F0E6] border border-[#E8DCC8] p-4">
            <p className="text-xs uppercase tracking-wider text-[#7C7064] mb-2">Aperçu</p>
            {preview ? (
              <iframe title="Aperçu newsletter" srcDoc={preview}
                className="w-full bg-white border border-[#E8DCC8]" style={{ height: 680 }}/>
            ) : (
              <div className="w-full bg-white border border-[#E8DCC8] flex items-center justify-center text-sm text-[#7C7064]" style={{ height: 680 }}>
                Cliquez sur « Prévisualiser » pour voir le rendu final.
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "history" && (
        <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F0E6] text-left">
              <tr>
                <th className="p-3">Date d'envoi</th>
                <th className="p-3">Objet</th>
                <th className="p-3">Destinataires</th>
                <th className="p-3">Envoyés / Échecs</th>
                <th className="p-3">Ouvertures · Clics</th>
                <th className="p-3">Statut</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 && (
                <tr><td colSpan="6" className="p-10 text-center text-[#7C7064]">Aucune campagne envoyée pour l'instant.</td></tr>
              )}
              {campaigns.map(c => (
                <tr key={c.campaign_id} className="border-t border-[#E8DCC8]">
                  <td className="p-3 text-xs text-[#7C7064]">{fmt(c.created_at)}</td>
                  <td className="p-3 font-serif">{c.subject}</td>
                  <td className="p-3">{c.recipients_total}</td>
                  <td className="p-3">
                    <span className="text-[#7B8A72]">{c.sent_count} envoyés</span>
                    {c.failed_count > 0 && <span className="text-[#A86962] ml-2">/ {c.failed_count} échecs</span>}
                  </td>
                  <td className="p-3" data-testid={`nl-stats-${c.campaign_id}`}>
                    {c.stats ? (
                      <div className="text-xs leading-relaxed">
                        <p><span className="font-serif text-base text-[#2C2621]">{c.stats.open_rate}%</span> <span className="text-[#7C7064]">ouvert ({c.stats.opened}/{c.stats.tracked})</span></p>
                        <p><span className="font-serif text-base text-[#2C2621]">{c.stats.click_rate}%</span> <span className="text-[#7C7064]">cliqué ({c.stats.clicked})</span>{c.stats.bounced > 0 && <span className="text-[#A86962]"> · {c.stats.bounced} rebond</span>}</p>
                      </div>
                    ) : <span className="text-xs text-[#7C7064]">—</span>}
                    {c.status === "sent" && (
                      <button data-testid={`nl-refresh-stats-${c.campaign_id}`} onClick={() => refreshStats(c.campaign_id)} disabled={statsLoading === c.campaign_id}
                        className="mt-1 text-xs text-[#A3876A] underline-offset-2 hover:underline">
                        {statsLoading === c.campaign_id ? "…" : (c.stats ? "Actualiser" : "Voir les statistiques")}
                      </button>
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    {c.status === "sent" ? (
                      <span className="inline-flex items-center gap-1 text-[#7B8A72]"><CheckCircle2 size={13}/> envoyée</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#A3876A]"><Clock size={13}/> en cours</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal confirmation d'envoi */}
      {confirmOpen && (
        <div data-testid="nl-confirm-modal" className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-[#FDFBF7] border border-[#E8DCC8] p-8 max-w-md">
            <h3 className="font-serif text-2xl mb-3">Confirmer l'envoi</h3>
            <p className="text-[#4A4036] mb-2">Votre newsletter va être envoyée à</p>
            <p className="font-serif text-4xl text-[#A3876A] mb-4">{audience} inscrite{audience>1?"s":""}</p>
            <p className="text-sm text-[#7C7064] mb-6">
              Objet : <em className="font-serif">« {subject} »</em>
              <br/>
              L'envoi ne peut pas être annulé une fois lancé.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={()=>setConfirmOpen(false)} className="px-4 py-2 border border-[#E8DCC8] text-[#4A4036] text-sm">Annuler</button>
              <button data-testid="nl-confirm-send" onClick={doSend} className="px-4 py-2 bg-[#A3876A] text-white text-sm">
                Envoyer maintenant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
